import { useState } from 'react'

import { useScaleChoice } from './scale-context.js'

/** The scales the theme declares. Anything else is ignored rather than trusted. */
const SCALES = ['default', 'compact', 'tv'] as const

export type DisplayScale = (typeof SCALES)[number]

const STORAGE_KEY = 'hg_scale'

function isScale(value: string | null): value is DisplayScale {
  return value !== null && (SCALES as readonly string[]).includes(value)
}

/** Reads storage without letting a locked-down browser take the app down with it. */
function remembered(): DisplayScale | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isScale(stored) ? stored : null
  } catch {
    return null
  }
}

export function remember(scale: DisplayScale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, scale)
  } catch {
    // A kiosk with storage disabled still gets the scale for this session, from
    // the URL it was launched with. Losing the memory is not worth an error.
  }
}

/** What the URL or the browser's memory says, before anybody chooses. */
export function firstChoice(): DisplayScale {
  // The URL is read rather than applied afterwards — a kiosk should not show
  // one frame at the wrong size.
  const asked = new URLSearchParams(window.location.search).get('scale')

  return isScale(asked) ? asked : (remembered() ?? 'default')
}

/**
 * How big everything should be.
 *
 * The television scale was built for `/host`, where nobody is playing — the
 * board is watched from a sofa. A Raspberry Pi in kiosk mode is the same
 * viewport with somebody sitting in front of it playing, so the ordinary app
 * needs to reach that scale too.
 *
 * `?scale=tv` sets it and is remembered, which suits a kiosk exactly: the
 * browser is launched with a fixed URL once, and every navigation after that
 * keeps the scale without carrying the query string around. It can also be
 * chosen on the profile page, which is how somebody who tried it on a laptop
 * gets back — and is why this reads from one shared place rather than keeping
 * its own copy, as it used to.
 */
export function useDisplayScale(): DisplayScale {
  const chosen = useScaleChoice()

  /*
   * Outside a provider this reads for itself, exactly as it always did.
   *
   * Both layouts supply one — the ordinary shell from the browser's memory,
   * the host display fixed at television — so in the running app this branch
   * is not reached. What reaches it is a component rendered on its own in a
   * test, which is a real caller and the reason it stays.
   *
   * An earlier version of this comment said the host display had no provider.
   * It had one, from above the router, which meant opening /host wrote a size
   * into the browser. The comment was wrong and the wiring is what changed.
   */
  const [standalone] = useState<DisplayScale>(firstChoice)

  return chosen?.scale ?? standalone
}
